import { Check, HandHeart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANDING_CONTENT } from "../content";
import { DropPhotoCta } from "./DropPhotoCta";
import { PhotoSlot } from "./PhotoSlot";

// Warm sand base matching the Stage's own surround, so the very first paint is a
// lit studio (never a white flash) and the hero reads as one warm room.
const GOLDEN_HOUR_BG =
  "radial-gradient(125% 90% at 78% 20%, #f6ecd4 0%, #efe3c8 45%, #e7ddca 100%)";

// The hero is a SPLIT composition (ADR-0055): copy + CTA on the left, a clear
// product image on the right, so the text is always legible and the pillow is
// always shown (retiring the text-over-full-bleed poster where the product was
// invisible). Still ZERO three.js on `/` — the right side is a static image, not
// a WebGL canvas (ADR-0040 §4, ADR-0041 §7).
export function LandingHero() {
  const { t } = useTranslation("landing");
  const reassurance = t("hero.reassurance", { returnObjects: true }) as string[];

  return (
    <section
      aria-labelledby="hero-heading"
      className="relative isolate flex min-h-[70svh] items-center overflow-hidden"
      style={{ background: GOLDEN_HOUR_BG }}
    >
      {/* min-h 74svh (a deliberate half-measure, not full 100): the first fold
          fills most of the screen but always leaves the NEXT section's heading
          ("Czy naprawdę będzie…") peeking, so the scroll-for-proof cue is explicit
          and it never reads as a "false bottom". svh, so the mobile URL bar doesn't
          jump. The peek is tuned together with ProofIsReal's reduced top padding. */}
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-6 pb-[clamp(2.5rem,5vw,4rem)] pt-[clamp(5.5rem,8vw,7rem)] sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
        <div className="min-w-0 max-w-xl duration-700 ease-out animate-in fade-in slide-in-from-bottom-4 motion-reduce:animate-none">
          <h1
            id="hero-heading"
            className="text-balance font-display text-[clamp(2.5rem,1.5rem+3.8vw,4rem)] font-semibold leading-[1.05] tracking-tight text-foreground"
          >
            {t("hero.headline")}
          </h1>
          <p className="mt-5 max-w-lg text-pretty text-[clamp(1.1rem,1rem+0.5vw,1.3rem)] leading-relaxed text-foreground/80">
            {t("hero.subhead")}
          </p>

          <div className="mt-8 flex flex-col items-start gap-3">
            <DropPhotoCta
              label={t("hero.cta")}
              className="min-h-13 px-9 text-base shadow-lg shadow-primary/25"
              testId="hero-cta"
            />
            <p className="text-sm text-foreground/65">{t("hero.ctaHint")}</p>
          </div>

          {Array.isArray(reassurance) ? (
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {reassurance.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-foreground/70">
                  <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="duration-1000 ease-out animate-in fade-in zoom-in-95 motion-reduce:animate-none">
          <PhotoSlot
            image={LANDING_CONTENT.hero.poster}
            alt={t("hero.posterAlt")}
            briefId="hero"
            aspectClassName="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]"
            icon={HandHeart}
            loading="eager"
            priority
            label={t("hero.posterPlaceholder")}
            className="shadow-2xl shadow-black/10 lg:max-h-[48svh]"
          />
        </div>
      </div>
    </section>
  );
}
